import {
  ButtonDesigns,
  ButtonTypes,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import Button from 'src/components/Button/Button'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import MultipleVirtualCardsForm, {
  MultipleVirtualCardsFormFlatFields,
} from 'src/components/MultipleVirtualCardsForm/MultipleVirtualCardsForm'
import SoftAlertBox from 'src/components/SoftAlertBox/SoftAlertBox'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { VirtualCardPushTaskListModel } from 'src/models/virtualCardPushTask.models'
import { registerOrganizationUrl } from 'src/navigation/urls'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { css } from 'styled-components'

interface CreateVirtualCardMultipleContentProps {
  labelCreditCardsAsAccounts: boolean
  isFormDisabled: boolean
  creditCardOptions: CreditCardOption[]
  errorDetailMap?: ErrorDetailMap<string>
  getCreditCardBulkTemplate: (type: string) => void
  postCreditCardBulkVirtualCards: (
    creditCardId: string,
    formData: FormData,
  ) => void
  postingCreditCardBulkVirtualCards: boolean
  setScreen: (val: CreateVirtualCardScreen) => void
  topLevelError?: string
  virtualCardPushTasks?: VirtualCardPushTaskListModel[]
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  pageLocation: string
  userHasOrg?: boolean
  userHasActiveRCNs?: boolean
  showSoftAlert?: boolean
  isBillPay?: boolean
}

const CreateVirtualCardMultipleContent: React.FC<
  CreateVirtualCardMultipleContentProps
> = (props) => {
  const translate = useTranslate()

  const queryParams = useSelector(selectQueryParams)
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const { analytics } = useAnalyticsContext()
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [StatisticsKeys.CreditCardsActiveDelegateCount] as const,
  })
  const hasBudgets =
    (statisticsResults[StatisticsKeys.CreditCardsActiveDelegateCount].data ??
      0) > 0
  const guestCardImage = useColorOrGrayscaleImage(ImageNames.CardTypeGuest)
  const multipleCardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeMultiple,
  )

  const disabledWithBudgets = !props.userHasOrg && hasBudgets
  const disabledWithoutBudgets = !props.userHasOrg && !hasBudgets

  const onSuccessfulSubmit = (
    flatFields: MultipleVirtualCardsFormFlatFields,
  ): void => {
    if (flatFields.creditCardId !== undefined && flatFields.csv !== undefined) {
      const formData = new FormData()
      formData.append('file', flatFields.csv, flatFields.csv.name)
      props.postCreditCardBulkVirtualCards(flatFields.creditCardId, formData)
    }
    analytics?.track(props.pageLocation + ' - bulk CSV upload - button press', {
      interaction: 'button press',
      intent: 'create virtual card',
      typeOfCard: 'Bulk CSV upload',
      sourceAccount:
        flatFields.creditCardId !== undefined
          ? creditCards[flatFields.creditCardId].displayName
          : undefined,
      fileUploaded: !!flatFields.csv,
    })
  }

  const initialFlatFields = {
    creditCardId:
      props.creditCardOptions.length === 1
        ? props.creditCardOptions[0].value
        : // TODO: refactor all uses of queryParams to handle arrays and normalize to string[]
          typeof queryParams.creditCardId === 'string'
          ? queryParams.creditCardId
          : undefined,
  }

  const flyoutProps = props.isBillPay
    ? {
        testId: 'billpayCSVUploadFlyout',
        title: translate('Pay Multiple Invoices'),
        pickerSubtitle: translate(
          'Upload a CSV file to create virtual cards and pay multiple invoices',
        ),
        specificElements: {
          imageSrc: guestCardImage,
        },
      }
    : {
        testId: 'vcCSVUploadFlyout',
        title: translate('Create a Virtual Card'),
        specificElements: {
          subtitle: translate('Bulk CSV Upload'),
          imageSrc: multipleCardImage,
        },
      }

  return (
    <ActionRightFlyoutContent {...flyoutProps}>
      {disabledWithBudgets && (
        <SoftAlertBox
          css={css`
            margin-bottom: 20px;
          `}
        >
          <div
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <div
              css={css`
                font-family: ${styleConstants.fontFamilies.medium};
              `}
            >
              {translate('Card types are changing!')}
            </div>
            <div
              css={css`
                margin-top: 20px;
              `}
            >
              {translate(
                'To access the budget, you need to be a part of the org. ${cta}',
                {
                  cta: translate(
                    'Please contact the Owner or Admin to get an invitation.',
                  ),
                },
              )}
            </div>
            <div
              css={css`
                display: flex;
              `}
            >
              <Button
                css={css`
                  margin-top: 20px;
                `}
                linkProps={{
                  to: registerOrganizationUrl(),
                  ariaLabelSource: 'Review and confirm org',
                }}
                design={ButtonDesigns.designCta}
                type={ButtonTypes.button}
                text={translate('Review and confirm org')}
              />
            </div>
          </div>
        </SoftAlertBox>
      )}
      {disabledWithoutBudgets && (
        <SoftAlertBox
          css={css`
            margin-bottom: 20px;
          `}
        >
          <div
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <div
              css={css`
                font-family: ${styleConstants.fontFamilies.medium};
              `}
            >
              {translate('Card types are changing!')}
            </div>
            <div
              css={css`
                margin-top: 20px;
              `}
            >
              {translate(
                "Please confirm your org and team to keep using these cards (we've filled things out for you to speed things up).",
              )}
            </div>
            <div
              css={css`
                display: flex;
              `}
            >
              <Button
                css={css`
                  margin-top: 20px;
                `}
                linkProps={{
                  to: registerOrganizationUrl(),
                  ariaLabelSource: 'Review and confirm org',
                }}
                design={ButtonDesigns.designCta}
                type={ButtonTypes.button}
                text={translate('Review and confirm org')}
              />
            </div>
          </div>
        </SoftAlertBox>
      )}
      <MultipleVirtualCardsForm
        css={css`
          ${disabledWithBudgets &&
          css`
            opacity: 0.5;
            pointer-events: none;

            #CreditCardAndCsvBox {
              border: 1px solid ${styleConstants.colors.yellow_dark};
            }
          `}
        `}
        labelCreditCardsAsAccounts={props.labelCreditCardsAsAccounts}
        creditCardOptions={props.creditCardOptions}
        getCreditCardBulkTemplate={props.getCreditCardBulkTemplate}
        onSuccessfulSubmit={onSuccessfulSubmit}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        initialFlatFields={initialFlatFields}
        postingCreditCardBulkVirtualCards={
          props.postingCreditCardBulkVirtualCards
        }
        virtualCardPushTasks={props.virtualCardPushTasks}
        creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
        userHasOrg={!!props.userHasOrg}
        userHasActiveRCNs={!!props.userHasActiveRCNs}
        isBillPay={props.isBillPay}
      />
    </ActionRightFlyoutContent>
  )
}

export default CreateVirtualCardMultipleContent
